// src/components/mdx/wrappers/ClientFootnotesWrapper.client.tsx

/**
 * @fileoverview Client-side footnote enhancement wrapper for MDX content.
 * @description Provides smooth scrolling, highlighting animations, and dynamic footnote collection
 * for footnote references and lists. This component enhances server-rendered footnotes with
 * interactive JavaScript functionality while maintaining accessibility and semantic structure.
 * @module ClientFootnotesWrapper
 */

'use client'

import { useEffect, useRef, Suspense } from 'react'

import {
  renderFootnoteItemHTML,
  isRenderableFootnoteItem,
} from '~/components/mdx/shared/footnote-item'

/**
 * Page-level footnote enhancement component.
 * 
 * Adds global footnote functionality to any page:
 * - Injects CSS animations for footnote highlighting
 * - Adds click handlers for smooth scrolling between footnotes and references
 * - Dynamically collects and renders footnotes from MDX content
 * 
 * This component renders nothing but provides global enhancements via side effects.
 * Mount once per page - it will enhance all footnote links on the page.
 * 
 * @returns {null} This component only provides side effects
 */
function FootnoteEnhancement(): null {
  const stylesInjectedRef = useRef<boolean>(false)

  // Style injection effect - runs once on mount
  useEffect(() => {
    // Prevent duplicate style injection across component re-mounts
    if (stylesInjectedRef.current || document.querySelector('[data-footnote-styles]')) {
      return
    }

    try {
      const style = document.createElement('style')
      style.setAttribute('data-footnote-styles', 'true')
      style.innerHTML = `
        @keyframes footnote-highlight-pulse {
          0% { background-color: rgba(59, 130, 246, 0); }
          30% { background-color: rgba(59, 130, 246, 0.15); }
          70% { background-color: rgba(59, 130, 246, 0.15); }
          100% { background-color: rgba(59, 130, 246, 0); }
        }
        
        @keyframes reference-highlight-pulse {
          0% { background-color: rgba(79, 70, 229, 0); }
          30% { background-color: rgba(79, 70, 229, 0.15); }
          70% { background-color: rgba(79, 70, 229, 0.15); }
          100% { background-color: rgba(79, 70, 229, 0); }
        }
        
        .footnote-highlight {
          animation: footnote-highlight-pulse 1.5s ease-in-out;
          border-radius: 0.5rem;
          transition: all 0.3s ease;
        }
        
        .reference-highlight {
          animation: reference-highlight-pulse 1.5s ease-in-out;
          border-radius: 0.25rem;
          transition: all 0.3s ease;
        }
        
        @media (prefers-color-scheme: dark) {
          @keyframes footnote-highlight-pulse {
            0% { background-color: rgba(96, 165, 250, 0); }
            30% { background-color: rgba(96, 165, 250, 0.1); }
            70% { background-color: rgba(96, 165, 250, 0.1); }
            100% { background-color: rgba(96, 165, 250, 0); }
          }
          
          @keyframes reference-highlight-pulse {
            0% { background-color: rgba(99, 102, 241, 0); }
            30% { background-color: rgba(99, 102, 241, 0.1); }
            70% { background-color: rgba(99, 102, 241, 0.1); }
            100% { background-color: rgba(99, 102, 241, 0); }
          }
        }
      `
      document.head.appendChild(style)
      stylesInjectedRef.current = true

      // Cleanup function to remove styles when component unmounts
      return () => {
        try {
          if (document.head.contains(style)) {
            document.head.removeChild(style)
          }
        } catch {
          // Silent cleanup
        }
      }
    } catch {
      // Silent error handling
    }
  }, [])

  // Click handler effect - runs once on mount
  useEffect(() => {
    /**
     * Smoothly scrolls to a footnote element with dynamic positioning and highlighting.
     * 
     * @param {HTMLElement | null} element - Target element to scroll to
     * @param {number} [offset=100] - Base offset from top of viewport
     * @param {'to-footnote' | 'to-reference'} [direction='to-footnote'] - Scroll direction for positioning
     * @returns {void}
     */
    function smoothScrollToElement(
      element: HTMLElement | null, 
      offset = 100, 
      direction: 'to-footnote' | 'to-reference' = 'to-footnote'
    ): void {
      if (!element) return

      try {
        // Check if element is still in the DOM to prevent errors
        if (!element.parentNode) {
          return
        }
        
        // Calculate dynamic positioning based on viewport and direction
        const elementRect = element.getBoundingClientRect()
        const absoluteElementTop = elementRect.top + window.scrollY
        const viewportHeight = window.innerHeight
        
        // Different positioning for footnotes vs references for better UX
        const verticalPosition = direction === 'to-footnote' 
          ? viewportHeight * 0.33  // Position footnotes in upper third
          : viewportHeight * 0.25  // Position references in upper quarter
        
        const dynamicOffset = Math.max(offset, verticalPosition)
        const targetPosition = Math.max(0, absoluteElementTop - dynamicOffset)

        // Optimize for smooth scrolling performance
        element.style.willChange = 'transform'
        window.scrollTo({ top: targetPosition, behavior: 'smooth' })

        // Add highlighting animation after scroll completes
        window.setTimeout(() => {
          try {
            // Double-check element is still in DOM before manipulating classes
            if (!element.parentNode) {
              return
            }
            
            // Apply appropriate highlight animation based on direction
            if (direction === 'to-footnote') {
              element.classList.add('footnote-highlight')
              window.setTimeout(() => element.classList.remove('footnote-highlight'), 1500)
            } else {
              element.classList.add('reference-highlight')
              window.setTimeout(() => element.classList.remove('reference-highlight'), 1500)
            }
          } catch {
            // Silent error handling
          }
        }, 300)
      } catch {
        // Silent error handling
      }
    }

    /**
     * Handles clicks on footnote links with enhanced navigation and error handling.
     * 
     * @param {MouseEvent} e - Click event from footnote link
     * @returns {void}
     */
    function handleFootnoteClick(e: MouseEvent): void {
      const target = e.target as HTMLElement
      const link = target.closest('a[href*="#footnote"]')
      
      if (!link) return
      
      // Prevent default anchor behavior and event bubbling
      e.preventDefault()
      e.stopPropagation()
      
      const href = link.getAttribute('href')
      if (!href) return
      
      const targetId = href.split('#')[1]
      if (!targetId) return
      
      try {
        // Determine navigation direction based on target ID pattern
        const isBacklink = targetId.startsWith('footnote-ref-')
        const direction = isBacklink ? 'to-reference' : 'to-footnote'
        const targetElement = document.getElementById(targetId)
        
        if (targetElement) {
          // Verify element is still in DOM before scrolling
          if (!targetElement.parentNode) {
            return
          }
          smoothScrollToElement(targetElement, 120, direction)
          
          // Use replaceState to update URL without triggering page re-render
          window.history.replaceState(null, '', `#${targetId}`)
        } else {
          // Handle cases where specific footnote element doesn't exist
          if (!isBacklink) {
            const footnoteList = document.querySelector('[role="doc-endnotes"]')
            if (footnoteList) {
              smoothScrollToElement(footnoteList as HTMLElement, 120, 'to-footnote')
              window.history.replaceState(null, '', `#${targetId}`)
            } else {
              // Fallback to browser default hash navigation
              window.location.hash = targetId
            }
          } else {
            // For backlinks without target, scroll to top
            window.scrollTo({ top: 0, behavior: 'smooth' })
            window.history.replaceState(null, '', `#${targetId}`)
          }
        }
      } catch {
        // Fallback to standard hash navigation on any error
        try {
          window.location.hash = targetId
        } catch {
          // Silent error handling
        }
      }
    }

    // Attach global click handler for all footnote links
    document.addEventListener('click', handleFootnoteClick)
    
    return () => {
      document.removeEventListener('click', handleFootnoteClick)
    }
  }, [])

  // Footnote collection effect - runs AFTER client has loaded
  useEffect(() => {
    /**
     * Collects footnote references from DOM and dynamically renders footnote list.
     * Matches the exact structure and styling of server-rendered footnotes.
     * Uses comprehensive data attributes to reconstruct missing footnotes from nested components.
     * 
     * @returns {void}
     */
    function collectAndUpdateFootnotes(): void {
      try {
        // Find all footnote references and the footnote list container
        const footnoteRefs = document.querySelectorAll('sup[data-footnote-id]')
        const footnoteList = document.querySelector('[role="doc-endnotes"]')
        
        if (!footnoteList || footnoteRefs.length === 0) {
          return
        }
        
        // Find the list container within the footnote list structure
        const listContainer = footnoteList.querySelector('[role="list"]')
        if (!listContainer) {
          return
        }
        
        // Always rebuild footnotes using comprehensive data attributes
        // This ensures we capture all footnotes, including those nested in components
        listContainer.innerHTML = ''
        
        // Collect unique footnotes from references using Map for deduplication
        const collectedFootnotes = new Map<string, { 
          id: string
          citation: string
          type: string
          url?: string 
        }>()
        
        // Extract footnote data from each reference element using comprehensive data attributes
        footnoteRefs.forEach((ref) => {
          const footnoteId = ref.getAttribute('data-footnote-id')
          if (!footnoteId) return
          
          // Use comprehensive data attributes for reliable data extraction
          let citation = ref.getAttribute('data-footnote-citation') ?? ''
          const type = ref.getAttribute('data-footnote-type') ?? 'website'
          const url = ref.getAttribute('data-footnote-url') ?? undefined
          
          // Fallback: Extract from title attribute if data attributes are missing (legacy support)
          if (!citation) {
            const link = ref.querySelector('a')
            if (link) {
              const title = link.getAttribute('title') ?? ''
              const extractedCitation = title.replace(`Go to footnote ${footnoteId}: `, '')
              if (extractedCitation && extractedCitation !== title) {
                citation = extractedCitation
              }
            }
          }
          
          // Skip if we still don't have citation data
          if (!citation) {
            return
          }
          
          // Add to collection if not already present
          if (!collectedFootnotes.has(footnoteId)) {
            collectedFootnotes.set(footnoteId, { 
              id: footnoteId, 
              citation, 
              type, 
              url: url ?? undefined 
            })
          }
        })
        
        // Sort footnotes by numeric ID for consistent ordering
        const sortedFootnotes = Array.from(collectedFootnotes.values())
          .sort((a, b) => parseInt(a.id) - parseInt(b.id))
        
        // Hide footnote list if no footnotes found
        if (sortedFootnotes.length === 0) {
          (footnoteList as HTMLElement).style.display = 'none'
          return
        }
        
        // Show the footnote list
        (footnoteList as HTMLElement).style.display = 'block'

        // Render footnotes from the SHARED item builder — the SAME source the
        // server (`FootnoteList` in `Footnotes.ssr.tsx`) renders from — so the
        // rebuilt markup is byte-identical to the server output and the two
        // paths can never drift. The wipe-and-rebuild above is preserved because
        // it is load-bearing: it recovers footnotes emitted by client-only MDX
        // components that the server `cache()` collector never saw.
        listContainer.innerHTML = sortedFootnotes
          // SHARED renderable-item predicate — the SAME include/exclude decision
          // the server `FootnoteList` applies, so empty/whitespace citations are
          // dropped identically on both paths (no SSR-vs-hydrated mismatch).
          .filter(isRenderableFootnoteItem)
          .map((footnote) => renderFootnoteItemHTML(footnote))
          .join('')

      } catch {
        // Silent error handling
      }
    }
    
    // Implement retry mechanism to ensure DOM is ready before collection
    let retryCount = 0
    const maxRetries = 10
    
    /**
     * Attempts footnote collection with retry logic for dynamic content.
     * 
     * @returns {void}
     */
    function attemptCollection(): void {
      const footnoteRefs = document.querySelectorAll('sup[data-footnote-id]')
      const footnoteList = document.querySelector('[role="doc-endnotes"]')
      
      if (footnoteRefs.length > 0 && footnoteList) {
        collectAndUpdateFootnotes()
      } else if (retryCount < maxRetries) {
        retryCount++
        setTimeout(attemptCollection, 200)
      }
    }
    
    // Start collection after initial delay to ensure client has loaded
    // Increased delay to ensure server-rendered content is fully available
    setTimeout(attemptCollection, 1000)
    
    // Set up MutationObserver to handle dynamically added content
    const observer = new MutationObserver((mutations) => {
      let shouldRetry = false
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
          // Check if any added nodes contain footnote references
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              const element = node as Element
              if (element.querySelector?.('sup[data-footnote-id]')) {
                shouldRetry = true
              }
            }
          })
        }
      })
      
      if (shouldRetry) {
        setTimeout(collectAndUpdateFootnotes, 50)
      }
    })
    
    // Observe the entire document for changes to catch dynamic content
    observer.observe(document.body, {
      childList: true,
      subtree: true
    })
    
    return () => {
      observer.disconnect()
    }
  }, [])

  // This component only provides side effects - renders nothing
  return null
}

/**
 * Main export component that wraps footnote enhancement in Suspense boundary.
 * Provides safe client-side footnote enhancements with fallback handling.
 * 
 * @returns {JSX.Element | null} Suspense-wrapped footnote enhancement or null
 */
export default function ClientFootnotesWrapper(): JSX.Element | null {
  return (
    <Suspense fallback={null}>
      <FootnoteEnhancement />
    </Suspense>
  )
}
